import * as React from 'react'
import { cva, type VariantProps } from 'class-variance-authority'
import { Slot } from 'radix-ui'

import { cn } from '@/lib/utils'

const buttonVariants = cva(
  [
    'inline-flex items-center justify-center gap-2 whitespace-nowrap',
    'rounded-md text-sm font-medium',
    'transition-[background,color,box-shadow,transform] duration-150 ease-out',
    'select-none cursor-pointer',
    'disabled:pointer-events-none disabled:opacity-50',
    "[&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 [&_svg]:shrink-0",
    'shrink-0 outline-none',
    'focus-visible:ring-[3px] focus-visible:ring-ring/40 focus-visible:border-ring',
    'aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive',
    'active:translate-y-px',
  ].join(' '),
  {
    variants: {
      variant: {
        default: [
          'bg-primary text-primary-foreground shadow-soft',
          'hover:bg-primary/90 hover:shadow-raised',
        ].join(' '),
        destructive: [
          'bg-destructive text-destructive-foreground shadow-soft',
          'hover:bg-destructive/90 focus-visible:ring-destructive/30',
          'dark:bg-destructive/85',
        ].join(' '),
        outline: [
          'border border-white/12 bg-white/[0.035] text-foreground shadow-soft',
          'hover:border-white/20 hover:bg-white/[0.07] hover:text-foreground',
        ].join(' '),
        secondary: [
          'border border-white/10 bg-secondary text-secondary-foreground shadow-soft',
          'hover:bg-white/[0.075]',
        ].join(' '),
        ghost: [
          'text-foreground/85',
          'hover:bg-white/[0.07] hover:text-foreground',
        ].join(' '),
        soft: [
          'bg-primary-soft text-primary-soft-foreground',
          'hover:bg-primary-soft/80',
        ].join(' '),
        link: 'text-primary underline-offset-4 hover:underline px-0 has-[>svg]:px-0',
      },
      size: {
        default: 'h-10 px-4 has-[>svg]:px-3.5',
        sm: 'h-8 rounded-md gap-1.5 px-3 has-[>svg]:px-2.5 text-xs',
        lg: 'h-11 rounded-md px-6 text-sm has-[>svg]:px-4',
        xl: 'h-12 rounded-lg px-7 text-[0.95rem]',
        icon: 'size-10',
        'icon-sm': 'size-8',
        'icon-lg': 'size-11',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
)

function Button({
  className,
  variant = 'default',
  size = 'default',
  asChild = false,
  ...props
}: React.ComponentProps<'button'> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean
  }) {
  const Comp = asChild ? Slot.Root : 'button'

  return (
    <Comp
      data-slot="button"
      data-variant={variant ?? 'default'}
      data-size={size ?? 'default'}
      className={cn(buttonVariants({ variant, size, className }))}
      {...props}
    />
  )
}

// eslint-disable-next-line react-refresh/only-export-components
export { Button, buttonVariants }
